/**
 * Cashflow Service
 * Frontend service for cashflow visibility API calls
 * Connects to backend API at /api/v1/cashflows
 */

import { APIClient, APIResponse } from './client';
import {
  Cashflow,
  CashflowResponse,
  CashflowQueryOptions,
  FlowDirection,
  BankAccountRef,
  MarkPaidRequest,
} from '../../types/cashflow';

// API client instance
const apiClient = new APIClient();

// Simulation request — user-supplied rate overrides for re-projection
export interface SimulateRequest {
  rateOverrides: Record<string, Record<string, number>>; // {rateType: {date_iso: new_rate}}
  days: number;
  startDate?: string; // YYYY-MM-DD
}

// Types for creating cashflows
export interface CashflowCreateData {
  amount: number;
  currency: string;
  flowDirection: FlowDirection;
  expectedDate: string; // YYYY-MM-DD format
  description: string;
  bankAccount?: BankAccountRef;
  workflowState?: string;
}

class CashflowService {
  /**
   * Get upcoming cashflows for a client
   * Fetches from Firestore via backend API
   */
  async getCashflows(
    clientId: string,
    options: CashflowQueryOptions = {}
  ): Promise<APIResponse<CashflowResponse>> {
    const { days = 30, includePending = true, startDate } = options;

    const params = new URLSearchParams({
      days: days.toString(),
      includePending: includePending.toString(),
    });
    if (startDate) params.set('start_date', startDate);

    return apiClient.get<CashflowResponse>(`/api/v1/cashflows/${clientId}?${params}`);
  }

  /**
   * Get unique currencies from cashflows (for filter dropdown)
   */
  async getAvailableCurrencies(clientId: string): Promise<APIResponse<string[]>> {
    return apiClient.get<string[]>(`/api/v1/cashflows/${clientId}/currencies`);
  }

  /**
   * Create a single manual cashflow entry
   */
  async createCashflow(
    clientId: string,
    data: CashflowCreateData
  ): Promise<APIResponse<Cashflow>> {
    return apiClient.post<Cashflow>(`/api/v1/cashflows/${clientId}`, data);
  }

  /**
   * Create multiple manual cashflow entries (batch)
   * Used for CSV upload
   */
  async createCashflowsBatch(
    clientId: string,
    cashflows: CashflowCreateData[]
  ): Promise<APIResponse<{ createdCount: number; ids: string[] }>> {
    return apiClient.post<{ createdCount: number; ids: string[] }>(
      `/api/v1/cashflows/${clientId}/batch`,
      cashflows
    );
  }

  /**
   * Delete a single manual cashflow entry
   */
  async deleteCashflow(
    clientId: string,
    cashflowId: string
  ): Promise<APIResponse<{ deletedId: string }>> {
    return apiClient.delete<{ deletedId: string }>(
      `/api/v1/cashflows/${clientId}/${cashflowId}`
    );
  }

  /**
   * Delete all manual cashflow entries for a client
   * Used for "Clear All" functionality
   */
  async deleteAllManualCashflows(
    clientId: string
  ): Promise<APIResponse<{ deletedCount: number }>> {
    return apiClient.delete<{ deletedCount: number }>(`/api/v1/cashflows/${clientId}`);
  }

  /**
   * Mark a trade cashflow as paid
   */
  async markCashflowPaid(
    clientId: string,
    cashflowId: string,
    data: MarkPaidRequest
  ): Promise<APIResponse<any>> {
    return apiClient.post<any>(
      `/api/v1/cashflows/${clientId}/${cashflowId}/mark-paid`,
      data
    );
  }

  /**
   * Simulate projected cashflows with user-supplied rate overrides.
   * Ephemeral — nothing is persisted.
   */
  async simulateCashflows(
    clientId: string,
    data: SimulateRequest
  ): Promise<APIResponse<CashflowResponse>> {
    return apiClient.post<CashflowResponse>(
      `/api/v1/cashflows/${clientId}/simulate`,
      data
    );
  }

  /**
   * Unmark a trade cashflow as paid
   */
  async unmarkCashflowPaid(
    clientId: string,
    cashflowId: string
  ): Promise<APIResponse<any>> {
    return apiClient.post<any>(
      `/api/v1/cashflows/${clientId}/${cashflowId}/unmark-paid`,
      {}
    );
  }
}

// Export singleton instance
export const cashflowService = new CashflowService();
